import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { getViews } from 'apis/ViewAPI';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  handleSetSelectedCommunity,
  handleSetSelectedParentCommunity,
  handleStoreViews,
} from 'store/slices/globalSlice';
import { getCommunitiesByIds, getCommunityById } from 'utils/helpers/communityMap';

import { COMMUNITY_RELATION_TYPE } from '../../constants';
import { COMPANY_COMMUNITY_RELATION_TYPE } from '../../constants/company';
import { sortViews } from '../View/utils';

interface Props {
  communityId: number;
}

const CommunityLoad = ({ communityId }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewId } = useParams();
  const dispatch = useAppDispatch();

  const { communityMap, views, selectedCommunity, contextCompany } = useAppSelector((state) => state.global);
  const { currentUser } = useAppSelector((state) => state.auth);
  const safeViews = views ?? [];

  useEffect(() => {
    if (communityId) {
      const community = getCommunityById(communityMap, communityId);

      if (!community) {
        return;
      }

      const selectedCommunity = {
        id: community.id,
        name: community.comunity_name,
        type: community.company_relation_type,
        address: community.address,
        state: community.state,
        unit_system: community.unit_system,
        preferences: community.preferences,
        company: {
          id: community.company_id,
          name: community.company_name,
        },
        parentCommunities: getCommunitiesByIds(communityMap, community.communities),
      };

      dispatch(handleSetSelectedCommunity(selectedCommunity));
      dispatch(handleSetSelectedParentCommunity(selectedCommunity?.parentCommunities[0] || null));
    }
  }, [communityId, communityMap]);

  const { isLoading, isFetching, isFetched } = useQuery({
    queryKey: ['communityViews', selectedCommunity, contextCompany],
    queryFn: () => getViews(selectedCommunity?.id || -1, {}),
    onSuccess: (data: any) => {
      const sortedViews = [...(Array.isArray(data) ? data : [])].sort(sortViews);
      dispatch(handleStoreViews(sortedViews));
    },
    enabled: Boolean(selectedCommunity?.id),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!isFetched || isLoading || isFetching) return;

    const showAICompetitiveIntelligence =
      currentUser?.feature_flags?.show_ai_competitive_intelligence && !currentUser?.isSuperuser;
    const defaultItem = safeViews.length > 0 && safeViews[0];
    const hasSelectedView = safeViews.some((view) => view?.id === Number(viewId));

    const communityRelationType =
      selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY
        ? COMMUNITY_RELATION_TYPE.COMMUNITY
        : COMMUNITY_RELATION_TYPE.COMPETITOR;

    const isRootCommunityPath = matchPath(`/${communityRelationType}/:communityId`, location.pathname);
    const isViewPath = matchPath(`/${communityRelationType}/:communityId/view/:viewId`, location.pathname);
    const shouldNavigateToHomepage =
      isRootCommunityPath &&
      selectedCommunity?.type == COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY &&
      showAICompetitiveIntelligence;

    if (shouldNavigateToHomepage) {
      navigate(`/community/${communityId}/ai-overview`);
      return;
    }

    const shouldNavigateToDefault = (isRootCommunityPath || isViewPath) && defaultItem && !hasSelectedView;
    if (shouldNavigateToDefault) {
      if (communityRelationType) {
        navigate(`/${communityRelationType}/${communityId}/view/${defaultItem?.id}`);
      }
    }
  }, [selectedCommunity, safeViews, viewId, isLoading, isFetched, isFetching, location.pathname]);

  return <Outlet />;
};

export default CommunityLoad;
